import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import { formatInTimeZone } from 'date-fns-tz';
import { rates as exchangeRates } from '../../data/exchangeRates';
import './Dashboard.css';
const API_URL = process.env.REACT_APP_API_URL;
const TranslatorJobsPage = ({ selectedCurrency }) => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  
  const [jobs, setJobs] = useState([]);
  const [balance, setBalance] = useState(0); // Bu state artık anlık hesaplanan bakiyeyi tutacak
  const [translatorTimezone, setTranslatorTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('bekleyen');

  useEffect(() => {
    const fetchPageData = async () => {
      if (!token) { setLoading(false); return; }
      try {
        setLoading(true); setError('');
        const [jobsResponse, profileResponse] = await Promise.all([
          fetch(`${API_URL}/api/dashboard/jobs`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${API_URL}/api/dashboard/profile`, { headers: { 'Authorization': `Bearer ${token}` } })
        ]);
        if (!jobsResponse.ok) { const errorData = await jobsResponse.json(); throw new Error(t(errorData.messageKey) || t('error_fetch_jobs')); }
        
        const jobsData = await jobsResponse.json();
        setJobs(jobsData);
        
        if (profileResponse.ok) {
            const profileData = await profileResponse.json();
            // DÜZELTME: Backend'den gelen ANLIK HESAPLANMIŞ bakiyeyi kullanıyoruz.
            setBalance(profileData.balance || 0);
			if(profileData.timezone) { setTranslatorTimezone(profileData.timezone); }
        } else {
            console.warn("Could not fetch profile data for balance.");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPageData();
  }, [token, t]);

// formatDate fonksiyonu artık dinamik olarak translatorTimezone'u kullanıyor
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return formatInTimeZone(new Date(dateString), translatorTimezone, 'dd.MM.yyyy HH:mm');
  };

  const JobStatus = ({ status }) => (
    <span className={`status-badge status-${status.toLowerCase()}`}>{t(`job_status_${status.toLowerCase()}`)}</span>
  );
const pendingJobs = useMemo(() => 
    jobs.filter(j => ['assigned', 'in_progress', 'in_review'].includes(j.status)),
    [jobs]
  );
  
  const completedJobs = useMemo(() =>
    jobs.filter(j => j.status === 'completed'),
    [jobs]
  );
  
  // DÜZELTME: "toplam" sekmesi kaldırıldı
 const filteredJobs = useMemo(() => {
    if (activeTab === 'bekleyen') return pendingJobs;
    if (activeTab === 'tamamlanan') return completedJobs;
    return jobs;
  }, [activeTab, pendingJobs, completedJobs, jobs]);

  const renderTableContent = () => {
    if (loading) { return <div className="table-row-message"><i className="fas fa-spinner fa-spin"></i> {t('loading_jobs')}</div>; }
    if (error) { return <div className="table-row-message error">{error}</div>; }
    if (filteredJobs.length === 0) { return <div className="table-row-message">{t('no_jobs_in_category')}</div>; }
    
    return filteredJobs.map(job => {
      const targetLangsArray = Array.isArray(job.target_langs) ? job.target_langs : [];
      return (
        <div className="table-row" key={job.id}>
          {/* Masaüstü için olan div'ler burada kalıyor */}
          <div className="cell-desktop" data-label={t('job_id')}>#{job.id}</div>
          <div className="cell-desktop" data-label={t('job_languages')}>{job.source_lang.toUpperCase()} &rarr; {targetLangsArray.join(', ').toUpperCase()}</div>
          <div className="cell-desktop" data-label={t('job_status')}><JobStatus status={job.status} /></div>
          <div className="cell-desktop" data-label={t('job_assigned_date')}>{formatDate(job.assigned_at)}</div>
          <div className="cell-desktop" data-label={t('job_deadline')}>{formatDate(job.deadline)}</div>
          
          <div className="cell-mobile">
            <div className="mobile-card-line">
              <span className="mobile-card-label">{t('job_languages')}</span>
              <span className="mobile-card-value">{job.source_lang.toUpperCase()} &rarr; {targetLangsArray.join(', ').toUpperCase()}</span>
            </div>
            <div className="mobile-card-line">
              <span className="mobile-card-label">{t('job_deadline')}</span>
              <span className="mobile-card-value">{formatDate(job.deadline)}</span>
            </div>
          </div>
          
          <div className="cell-action">
            <Link to={`/dashboard/jobs/${job.id}`} className="details-btn">{t('view_details_btn')}</Link>  </div>
        </div>
      );
    });
  };


const convertedBalance = useMemo(() => {
    const rate = exchangeRates[selectedCurrency] || 1;
    // DÜZELTME: 'balance' state'ini (TRY cinsinden) hedef kura çevir.
    return balance * rate;
  }, [balance, selectedCurrency]);

  return (
    <div className="dashboard-page">
      <div className="page-header modern">
         <h1 className="header-title">
            <span className="title-desktop">{t('dashboard_all_jobs')}</span>
            <span className="title-mobile">{t('translation_jobs', 'Tercüme İşleri')}</span>
        </h1>
        <div className="balance-box modern">
          {t('balance')}: 
          <strong>
            {convertedBalance.toLocaleString(i18n.language, {
              style: 'currency', 
              currency: selectedCurrency || 'TRY'
            })}
          </strong> 
          <i className="fas fa-question-circle"></i>
        </div>
      </div>


       <div className="jobs-list-container">
        <div className="status-tabs">
 <button onClick={() => setActiveTab('bekleyen')} className={activeTab === 'bekleyen' ? 'active' : ''}>
            {t('status_pending')} ({pendingJobs.length})
          </button>          {/* "Toplam Atanmış" sekmesi kaldırıldı */}
          <button onClick={() => setActiveTab('tamamlanan')} className={activeTab === 'tamamlanan' ? 'active' : ''}>{t('status_completed')} ({jobs.filter(j => j.status === 'completed').length})</button>
        </div>
        
        <div className="jobs-table">
          <div className="table-header">
            <div>{t('job_id')}</div>
            <div>{t('job_languages')}</div>
            <div>{t('job_status')}</div>
            <div>{t('job_assigned_date')}</div>
            <div>{t('job_deadline')}</div>
            <div></div>
          </div>
          <div className="table-body">
            {renderTableContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TranslatorJobsPage;